import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/api'; // Ensure correct import path
import './PageBanner.css';

const PageBanner = ({ pageKey, sliderImages, className, hideOverlay, title, subtitle }) => {
    const [banner, setBanner] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        const fetchBanner = async () => {
            try {
                // Fetch from API
                const response = await axios.get(`${API_URL}/banners/${pageKey}`);
                const data = response.data;

                if (data) {
                    setBanner({
                        // Handle Cloudinary (absolute) vs Local (relative) paths
                        imageUrl: data.image ? (data.image.startsWith('http') ? data.image : `${API_URL.replace('/api', '')}/${data.image}`) : '',
                        heading: data.title,
                        headingColor: '#ffffff', // Default or fetch from DB if extended
                        headingSize: '48px',
                        subheading: data.subtitle,
                        subheadingColor: '#ffffff',
                        subheadingSize: '24px',
                        enabled: data.enabled !== undefined ? data.enabled : true
                    });
                }
            } catch (error) {
                // If 404 or error, assume no banner
                console.log(`No banner for ${pageKey}`);
                setBanner(null);
            }
        };

        if (!title) {
            fetchBanner();
        } else {
            fetchBanner();
        }
    }, [pageKey, title]);

    // Auto Slide Logic
    useEffect(() => {
        if (sliderImages && sliderImages.length > 0) {
            const interval = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
            }, 3000); // 3 seconds interval
            return () => clearInterval(interval);
        }
    }, [sliderImages]);

    const showSlider = sliderImages && sliderImages.length > 0;

    // Don't render anything if no banner (and no slider) or disabled
    if (!showSlider && (!banner || !banner.imageUrl || (banner.enabled === false && !title))) {
        // logic tweak: if title exists, we might want to show even if banner disabled? 
        // For now keeping consistent: if no slider and no banner data, return null.
        if (title && !banner) {
            // If we have a title but no banner data yet (or failed fetch), and no slider, 
            // we might want to render just a placeholder or nothing. 
            // Let's assume for now we need image/slider to render background.
        }
        if (!title && (!banner || !banner.imageUrl || banner.enabled === false))
            return null;
    }

    const isHome = pageKey === 'home';

    return (
        <section className={`page-banner-section ${className || ''}`}>
            <div className={`page-banner-container ${isHome ? 'home-hero' : ''}`}>
                {showSlider ? (
                    <>
                        {sliderImages.map((img, index) => (
                            <div
                                key={index}
                                className={`banner-slide ${index === currentSlide ? 'active' : ''}`}
                            >
                                <img
                                    src={img}
                                    alt={`Slide ${index + 1}`}
                                    className="page-banner-image"
                                />
                            </div>
                        ))}
                        <div className="slider-dots">
                            {sliderImages.map((_, index) => (
                                <div
                                    key={index}
                                    className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                                    onClick={() => setCurrentSlide(index)}
                                />
                            ))}
                        </div>
                    </>
                ) : (
                    banner && banner.imageUrl && (
                        <img
                            src={banner.imageUrl}
                            alt={banner.heading || 'Page Banner'}
                            className="page-banner-image"
                        />
                    )
                )}

                {!hideOverlay && (
                    <>
                        <div className="page-banner-overlay"></div>
                        <div className="page-banner-content">
                            {(title || (banner && banner.heading)) && (
                                <h1
                                    className="page-banner-heading"
                                    style={{
                                        color: (banner && banner.headingColor) || '#ffffff',
                                        fontSize: isHome ? '4rem' : ((banner && banner.headingSize) || '48px')
                                    }}
                                >
                                    {title || banner.heading}
                                </h1>
                            )}
                            {(subtitle || (banner && banner.subheading)) && (
                                <p
                                    className="page-banner-subheading"
                                    style={{
                                        color: (banner && banner.subheadingColor) || '#ffffff',
                                        fontSize: isHome ? '1.8rem' : ((banner && banner.subheadingSize) || '24px')
                                    }}
                                >
                                    {subtitle || banner.subheading}
                                </p>
                            )}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default PageBanner;
